import type { ComponentType, HTMLAttributes, ReactNode } from "react";
import { CheckCircle2, Inbox } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * `"no-data"` — ausência ESTRUTURAL: não há nada cadastrado ainda, ou o
 * monitor não estava conectado no período. Não sabemos o número.
 * `"zero"`   — o número é conhecido e vale ZERO. "0 saídas hoje" é uma boa
 * notícia, não um vazio; pintar isso de "nada aqui" mente para o usuário.
 *
 * Por isso a distinção é prop explícita, nunca inferida de `items.length`.
 */
export type EmptyStateKind = "no-data" | "zero";

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  kind: EmptyStateKind;
  title?: ReactNode;
  description?: ReactNode;
  /** Ícone de LINHA (20px dentro de um círculo de 48px). Nunca ilustração colorida grande. */
  icon?: ComponentType<{ className?: string }>;
  /** No máximo 1 CTA. */
  action?: ReactNode;
  /** Versão enxuta para dentro de card pequeno. */
  compact?: boolean;
  /**
   * Micro-rótulo que nomeia o TIPO de vazio ("Zero medido" / "Sem medição").
   * Ligado por padrão: é o segundo canal da distinção 0 ≠ "sem dados".
   */
  showKind?: boolean;
}

const KIND_LABEL: Record<EmptyStateKind, string> = {
  "no-data": "Sem medição",
  zero: "Zero medido",
};

const DEFAULTS: Record<EmptyStateKind, { title: string; description: string }> = {
  "no-data": {
    title: "Sem dados neste período",
    description:
      "Não houve medição aqui — não é o mesmo que ter dado valor zero. Verifique o monitor ou amplie o intervalo.",
  },
  zero: {
    title: "Nenhum registro no período",
    description: "O período foi medido e o resultado é zero.",
  },
};

export function EmptyState({
  kind,
  title,
  description,
  icon,
  action,
  compact = false,
  showKind = true,
  className,
  ...props
}: EmptyStateProps) {
  const fallback = DEFAULTS[kind];
  const Icon = icon ?? (kind === "zero" ? CheckCircle2 : Inbox);

  return (
    <div
      data-kind={kind}
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-1.5 px-4 py-6" : "gap-1.5 px-6 py-10",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mb-1 inline-grid shrink-0 place-items-center rounded-full bg-muted text-muted-foreground",
          compact ? "size-10" : "size-12",
        )}
      >
        <Icon className={compact ? "size-4" : "size-5"} />
      </span>
      {showKind ? (
        <span className="font-mono text-2xs font-medium uppercase tracking-caps text-subtle-foreground">
          {KIND_LABEL[kind]}
        </span>
      ) : null}
      <p className="text-md text-foreground">{title ?? fallback.title}</p>
      <p className="max-w-sm text-sm text-muted-foreground text-pretty">
        {description ?? fallback.description}
      </p>
      {action ? <div className="mt-2.5 max-md:w-full">{action}</div> : null}
    </div>
  );
}
